/*
Copyright 2022-2024 New Vector Ltd.

SPDX-License-Identifier: AGPL-3.0-only OR LicenseRef-Element-Commercial
Please see LICENSE in the repository root for full details.
*/

import { type FC, useEffect, useState } from "react";
import { createClient } from "matrix-js-sdk";
import { useClient } from "../ClientContext";
import { initClient } from "../utils/matrix";
import { Config } from "../config/Config";

import commonStyles from "./common.module.css";

export const UnauthenticatedView: FC = () => {
  const { setClient } = useClient();
  const [isLoggingIn, setIsLoggingIn] = useState(false);

  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    const loginToken = params.get("loginToken");
    
    if (loginToken && setClient && !isLoggingIn) {
      setIsLoggingIn(true);
      const homeserver = Config.defaultHomeserverUrl();
      const authClient = createClient({ baseUrl: homeserver });
      
      authClient.login("m.login.token", { token: loginToken })
        .then(async (res: any) => {
          const { user_id, access_token, device_id } = res;
          const session = {
            user_id,
            access_token,
            device_id,
            passwordlessUser: false,
          };
          
          const newClient = await initClient(
            {
              baseUrl: homeserver,
              accessToken: access_token,
              userId: user_id,
              deviceId: device_id,
            },
            false,
          );
          
          setClient(newClient, session);
          window.history.replaceState({}, document.title, window.location.pathname);
        })
        .catch((err) => {
          console.error("SSO Token Login Failed:", err);
          setIsLoggingIn(false);
        });
    }
  }, [setClient, isLoggingIn]);

  // Force dark/plain background on this page (matching the login page)
  useEffect(() => {
    const body = document.getElementsByTagName("body")[0];
    const prevBg = body.style.backgroundImage;
    body.style.backgroundImage = "none";
    return () => {
      body.style.backgroundImage = prevBg;
    };
  }, []);

  return (
    <>
      <div
        className={commonStyles.container}
        style={{ minHeight: "100vh", display: "flex", flexDirection: "column" }}
      >
        <main
          className={commonStyles.main}
          style={{
            flex: 1,
            display: "flex",
            flexDirection: "column",
            alignItems: "center",
            justifyContent: "center",
            position: "relative",
          }}
        >
          {/* Top-left branding */}
          <div
            style={{
              position: "absolute",
              top: "20px",
              left: "20px",
              zIndex: 1000,
              display: "flex",
              alignItems: "center",
              gap: "15px",
            }}
          >
            <img
              src="/logo_mimosa_call.png"
              alt="Mimosa Call"
              style={{
                height: "60px",
                width: "60px",
                borderRadius: "12px",
                boxShadow: "0 4px 8px rgba(0,0,0,0.2)",
              }}
            />
            <div
              style={{ display: "flex", alignItems: "baseline", gap: "10px" }}
            >
              <span
                style={{
                  fontSize: "45px",
                  fontWeight: "700",
                  color: "#ffffff",
                  lineHeight: "60px",
                }}
              >
                Mimosa Call
              </span>
              <span
                style={{
                  fontSize: "18px",
                  fontStyle: "italic",
                  color: "#00f3ff",
                  fontWeight: "500",
                  lineHeight: "60px",
                }}
              >
                Convergence Visioconférence
              </span>
            </div>
          </div>

          {/* Fongola SSO Button */}
          <div style={{ textAlign: "center" }}>
            <div
              onClick={() => {
                window.location.href = `https://ma3x.ztn0.net/_matrix/client/v3/login/sso/redirect?redirectUrl=${encodeURIComponent(window.location.origin)}`;
              }}
              data-testid="login_sso"
              style={{
                display: "flex",
                flexDirection: "column",
                alignItems: "center",
                cursor: "pointer",
              }}
            >
              <img
                src="/fongola_icon.png"
                alt="Fongola SSO"
                style={{
                  width: "120px",
                  height: "120px",
                  marginBottom: "16px",
                  transition: "transform 0.2s",
                  borderRadius: "30px",
                  boxShadow: "0 4px 12px rgba(0,0,0,0.3)",
                }}
                onMouseOver={(e) =>
                  (e.currentTarget.style.transform = "scale(1.05)")
                }
                onMouseOut={(e) =>
                  (e.currentTarget.style.transform = "scale(1)")
                }
              />
              <span
                style={{
                  fontSize: "18px",
                  fontWeight: "500",
                  color: "#9ca3af",
                }}
              >
                Se connecter avec Convergence
              </span>
            </div>
          </div>
        </main>

        {/* Portal footer */}
        <footer
          style={{
            padding: "20px",
            textAlign: "center",
            fontSize: "13px",
            color: "rgba(255, 255, 255, 0.45)",
            lineHeight: "1.6",
            width: "100%",
            borderTop: "1px solid rgba(255, 255, 255, 0.05)",
            background: "transparent",
          }}
        >
          <div>Propulsé par D3X Integral Portal</div>
          <div
            style={{
              fontWeight: "500",
              color: "rgba(255, 255, 255, 0.35)",
            }}
          >
            L'accès à ce système est restreint.
          </div>
          <div
            style={{ marginTop: "8px", color: "rgba(255, 255, 255, 0.25)" }}
          >
            © 2026 Convergence Cloud Privé
          </div>
        </footer>
      </div>
    </>
  );
};
